import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowRight,
  Award,
  Check,
  CheckCircle2,
  Clock3,
  Flame,
  HelpCircle,
  RotateCcw,
  Sparkles,
  Trophy,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import StatCard from '../components/Dashboard/StatCard';
import { challengeAPI } from '../services/api';
import XPToast from '../components/ui/XPToast';
import SpeakButton from '../components/ui/SpeakButton';

const DURATION = 60; // seconds

// ── Built-in fallback questions (used when Supabase quiz_questions is empty) ──
const FALLBACK_QUESTIONS = [
  { id: 'f1',  question_text: 'Welcher Artikel passt? ___ Hund bellt laut.', options: ['Der', 'Die', 'Das', 'Den'], correct_answer: 'Der' },
  { id: 'f2',  question_text: 'Welches Verb ist richtig? Er ___ gestern ins Kino gegangen.', options: ['ist', 'hat', 'sein', 'war'], correct_answer: 'ist' },
  { id: 'f3',  question_text: 'Wie heißt die Pluralform? Das Kind → die ___', options: ['Kindes', 'Kinder', 'Kinds', 'Kinden'], correct_answer: 'Kinder' },
  { id: 'f4',  question_text: 'Welche Präposition? Ich fahre ___ Berlin.', options: ['nach', 'zu', 'in', 'an'], correct_answer: 'nach' },
  { id: 'f5',  question_text: 'Welcher Kasus? Ich gebe ___ Mann das Buch.', options: ['dem', 'den', 'der', 'des'], correct_answer: 'dem' },
  { id: 'f6',  question_text: 'Konjugiere: Du ___ heute sehr müde. (sein)', options: ['bist', 'ist', 'sind', 'seid'], correct_answer: 'bist' },
  { id: 'f7',  question_text: 'Perfekt: Ich ___ das Buch gelesen.', options: ['habe', 'bin', 'hatte', 'war'], correct_answer: 'habe' },
  { id: 'f8',  question_text: 'Was ist das Gegenteil von "groß"?', options: ['klein', 'kurz', 'eng', 'dünn'], correct_answer: 'klein' },
  { id: 'f9',  question_text: 'Welcher Artikel? ___ Sonne scheint hell.', options: ['Die', 'Der', 'Das', 'Den'], correct_answer: 'Die' },
  { id: 'f10', question_text: 'Welche Form ist korrekt? Wir ___ morgen reisen.', options: ['werden', 'sind', 'haben', 'wollen'], correct_answer: 'werden' },
  { id: 'f11', question_text: 'Komparativ: schnell → ___', options: ['schneller', 'am schnellsten', 'schnellst', 'schnelle'], correct_answer: 'schneller' },
  { id: 'f12', question_text: 'Welche Konjunktion? Ich komme, ___ ich Zeit habe.', options: ['wenn', 'weil', 'obwohl', 'damit'], correct_answer: 'wenn' },
  { id: 'f13', question_text: 'Passiv: Das Buch ___ von Maria geschrieben.', options: ['wurde', 'hat', 'ist', 'war'], correct_answer: 'wurde' },
  { id: 'f14', question_text: 'Akkusativ: Ich sehe ___ Hund.', options: ['den', 'dem', 'der', 'des'], correct_answer: 'den' },
  { id: 'f15', question_text: 'Si thuhet "Dëshiroj" në gjermanisht?', options: ['Ich möchte', 'Ich will', 'Ich kann', 'Ich darf'], correct_answer: 'Ich möchte' },
  { id: 'f16', question_text: 'Superlativ: hoch → am ___', options: ['höchsten', 'hochsten', 'hohen', 'höhsten'], correct_answer: 'höchsten' },
  { id: 'f17', question_text: 'Reflexivpronomen: Er wäscht ___ die Hände.', options: ['sich', 'ihm', 'ihn', 'seiner'], correct_answer: 'sich' },
  { id: 'f18', question_text: 'Cili fjalë do të thotë "prandaj" në gjermanisht?', options: ['deshalb', 'obwohl', 'damit', 'falls'], correct_answer: 'deshalb' },
  { id: 'f19', question_text: 'Genitiv: Das Auto ___ Mannes ist rot.', options: ['des', 'dem', 'den', 'der'], correct_answer: 'des' },
  { id: 'f20', question_text: 'Konjunktiv II: Wenn ich Zeit hätte, ___ ich reisen.', options: ['würde', 'werde', 'will', 'kann'], correct_answer: 'würde' },
  { id: 'f21', question_text: 'Was ist die Vergangenheitsform von "gehen"?', options: ['ging', 'geht', 'gegangen', 'gehte'], correct_answer: 'ging' },
  { id: 'f22', question_text: 'Welche Präposition (Dativ)? Das Buch liegt ___ dem Tisch.', options: ['auf', 'an', 'in', 'über'], correct_answer: 'auf' },
  { id: 'f23', question_text: 'Welcher Artikel? ___ Wasser ist kalt.', options: ['Das', 'Der', 'Die', 'Den'], correct_answer: 'Das' },
  { id: 'f24', question_text: 'Trennbares Verb: Sie ___ das Licht ___. (anmachen)', options: ['macht … an', 'machen … auf', 'macht … auf', 'machte … an'], correct_answer: 'macht … an' },
  { id: 'f25', question_text: 'Modal: Er ___ morgen arbeiten. (müssen)', options: ['muss', 'musst', 'müsst', 'möchte'], correct_answer: 'muss' },
];

const TimedChallengePage = () => {
  const [phase, setPhase]         = useState('idle'); // idle | countdown | playing | done
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx]             = useState(0);
  const [answers, setAnswers]     = useState([]);
  const [timeLeft, setTimeLeft]   = useState(DURATION);
  const [result, setResult]       = useState(null);
  const [xpToast, setXpToast]     = useState(null);
  const [selected, setSelected]   = useState(null);
  const timerRef = useRef(null);
  const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem('challengeBest') || '0', 10));

  const startGame = async () => {
    let qs = [];
    try {
      const res = await challengeAPI.getQuestions();
      qs = res.data.questions || [];
    } catch {
      /* fallback */
    }
    if (qs.length === 0) {
      qs = [...FALLBACK_QUESTIONS].sort(() => 0.5 - Math.random()).slice(0, 20);
    }
    setQuestions(qs);

    setPhase('countdown');
    let c = 3;
    setTimeLeft(c);
    const iv = setInterval(() => {
      c--;
      setTimeLeft(c);
      if (c <= 0) {
        clearInterval(iv);
        beginPlay();
      }
    }, 1000);
  };

  const beginPlay = () => {
    setPhase('playing');
    setIdx(0);
    setAnswers([]);
    setSelected(null);
    setTimeLeft(DURATION);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          finishGame();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const handleAnswer = (answer, correctAnswer) => {
    if (selected !== null) return;
    setSelected(answer);

    const current = questions[idx];
    const entry = {
      question_id: current.id,
      question_text: current.question_text,
      user_answer: answer,
      correct_answer: correctAnswer,
      is_correct: answer === correctAnswer,
    };
    const newAnswers = [...answers, entry];
    setAnswers(newAnswers);

    setTimeout(() => {
      if (idx + 1 >= questions.length) {
        clearInterval(timerRef.current);
        submitGame(newAnswers);
      } else {
        setIdx((i) => i + 1);
        setSelected(null);
      }
    }, 500);
  };

  const submitGameRef = useRef(null);

  const submitGame = async (finalAnswers) => {
    setPhase('done');
    try {
      const elapsed = DURATION - timeLeft;
      const res = await challengeAPI.submit(finalAnswers, elapsed);
      setResult(res.data);
      if (res.data.xp_awarded > 0) {
        setXpToast({ amount: res.data.xp_awarded, reason: 'timed_challenge' });
      }
      const newBest = Math.max(bestScore, res.data.correct || 0);
      setBestScore(newBest);
      localStorage.setItem('challengeBest', String(newBest));
    } catch (e) {
      console.error(e);
      // Fallback local score calculation if API offline
      const correctCount = finalAnswers.filter((a) => a.is_correct).length;
      const fallbackResult = {
        correct: correctCount,
        total: finalAnswers.length,
        score: finalAnswers.length ? Math.round((correctCount / finalAnswers.length) * 100) : 0,
        xp_awarded: correctCount * 5,
      };
      setResult(fallbackResult);
      const newBest = Math.max(bestScore, correctCount);
      setBestScore(newBest);
      localStorage.setItem('challengeBest', String(newBest));
    }
  };

  submitGameRef.current = submitGame;

  const finishGame = useCallback(() => {
    setAnswers((prev) => {
      if (submitGameRef.current) submitGameRef.current(prev);
      return prev;
    });
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const current = questions[idx];
  const urgentTime = timeLeft <= 10 && phase === 'playing';
  const liveCorrectCount = answers.filter((a) => a.is_correct).length;
  const progressPercent = questions.length ? Math.round(((idx) / questions.length) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* =========================================================
            HEADER - Unified with Chat / Dashboard Style
           ========================================================= */}
        <header className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>
          <div className="flex flex-col justify-between gap-4 p-5 sm:flex-row sm:items-center sm:p-7">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-zinc-900 sm:text-3xl">Sfida e Kohës</h1>
              <p className="mt-1 text-sm text-zinc-500">
                Testo shpejtësinë dhe njohuritë e tua në gjermanisht brenda 60 sekondash.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {phase === 'playing' && (
                <div className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-extrabold tabular-nums transition ${
                  urgentTime ? 'bg-red-50 text-[#DD0000] ring-1 ring-red-300' : 'bg-zinc-100 text-zinc-800'
                }`}>
                  <Clock3 size={15} className={urgentTime ? 'animate-pulse' : ''} />
                  00:{String(timeLeft).padStart(2, '0')}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* =========================================================
            PHASE 1: IDLE - Hero Card & Rules Overview
           ========================================================= */}
        {phase === 'idle' && (
          <section className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
            <div aria-hidden="true" className="flex h-1 shrink-0">
              <span className="flex-1 bg-zinc-950" />
              <span className="flex-1 bg-[#DD0000]" />
              <span className="flex-1 bg-[#FFCE00]" />
            </div>

            <div className="grid grid-cols-1 gap-6 p-6 sm:p-8 lg:grid-cols-12 lg:items-center">
              {/* Left Column (Hero Content) */}
              <div className="lg:col-span-7 space-y-5">
                <h2 className="text-2xl font-extrabold tracking-tight text-zinc-900 sm:text-4xl">
                  Sa shumë di brenda një minute?
                </h2>
                <p className="text-sm leading-relaxed text-zinc-600 sm:text-base">
                  Përgjigju sa më shumë pyetjeve të gramatikës, artikujve dhe fjalorit gjerman. Çdo përgjigje e saktë të shpërblen me <strong className="font-extrabold text-[#DD0000]">+5 XP</strong>.
                </p>

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={startGame}
                    className="inline-flex items-center gap-2.5 rounded-xl bg-zinc-950 px-6 py-3.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-[#DD0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0000] focus-visible:ring-offset-2"
                  >
                    Fillo Sfidën Tani <ArrowRight size={17} />
                  </button>
                </div>
              </div>

              {/* Right Column (personal record) */}
              <div className="lg:col-span-5">
                <StatCard
                  title="Rekordi yt"
                  value={bestScore}
                  unit="pyetje"
                  tone="gold"
                  badge={{ icon: Clock3, text: '60 sek' }}
                  notice={{ icon: Sparkles, text: '+5 XP për çdo përgjigje të saktë', tone: 'red' }}
                  footer="Raund i pafund – thyej rekordin"
                  action={{ label: 'Fillo', onClick: startGame }}
                />
              </div>
            </div>
          </section>
        )}

        {/* =========================================================
            PHASE 2: COUNTDOWN - Energetic 3, 2, 1
           ========================================================= */}
        {phase === 'countdown' && (
          <section className="flex min-h-[380px] flex-col items-center justify-center rounded-2xl border border-zinc-200/80 bg-white text-center shadow-sm">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-[#DD0000] shadow-sm">
              <Zap size={30} className="fill-[#DD0000]" />
            </div>
            <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.2em] text-zinc-500">Bëhu gati</p>
            <div className="mt-2 text-8xl font-black tabular-nums tracking-tight text-zinc-950 animate-pulse">
              {timeLeft || '🚀'}
            </div>
            <p className="mt-4 text-xs font-medium text-zinc-400">Përgatitu të zgjedhësh përgjigjen e duhur!</p>
          </section>
        )}

        {/* =========================================================
            PHASE 3: PLAYING - Full-Width Spacious Interactive Arena
           ========================================================= */}
        {phase === 'playing' && current && (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            {/* Left Main Card: Question & Answer Options */}
            <section className="lg:col-span-8 flex flex-col justify-between overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
              <div aria-hidden="true" className="flex h-1 shrink-0">
                <span className="flex-1 bg-zinc-950" />
                <span className="flex-1 bg-[#DD0000]" />
                <span className="flex-1 bg-[#FFCE00]" />
              </div>

              <div className="p-6 sm:p-8">
                {/* Question Header */}
                <div className="flex items-center justify-between gap-3 border-b border-zinc-100 pb-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-zinc-100 px-2.5 py-1 text-xs font-extrabold text-zinc-700">
                      Pyetja {idx + 1}
                    </span>
                    <span className="text-xs text-zinc-400">nga {questions.length}</span>
                  </div>
                  <SpeakButton text={current.question_text} lang="de-DE" />
                </div>

                {/* Question Body */}
                <div className="py-6 sm:py-8">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#DD0000]">
                    Zgjidh përgjigjen e saktë
                  </p>
                  <h2 className="mt-3 text-xl font-bold leading-relaxed text-zinc-900 sm:text-2xl md:text-3xl">
                    {current.question_text}
                  </h2>
                </div>

                {/* Options Grid (2x2 on sm+, or 1 col on mobile) */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {current.options.length > 0 ? (
                    current.options.map((opt, optionIndex) => {
                      const isSelected = selected === opt;
                      const isCorrect = opt === current.correct_answer;
                      let optionStyle =
                        'border-zinc-200 bg-white text-zinc-800 hover:border-zinc-400 hover:bg-zinc-50/80 shadow-xs';
                      let statusIcon = null;

                      if (selected !== null) {
                        if (isCorrect) {
                          optionStyle = 'border-emerald-500 bg-emerald-50 text-emerald-900 font-bold ring-1 ring-emerald-500';
                          statusIcon = <CheckCircle2 size={19} className="text-emerald-600 shrink-0" />;
                        } else if (isSelected) {
                          optionStyle = 'border-red-500 bg-red-50 text-red-900 font-bold ring-1 ring-red-500';
                          statusIcon = <XCircle size={19} className="text-red-600 shrink-0" />;
                        } else {
                          optionStyle = 'border-zinc-200/60 bg-zinc-50 text-zinc-400 opacity-50';
                        }
                      }

                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleAnswer(opt, current.correct_answer)}
                          disabled={selected !== null}
                          className={`flex min-h-[58px] items-center gap-3.5 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition disabled:cursor-default ${optionStyle}`}
                        >
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-extrabold ${
                            isSelected || (selected !== null && isCorrect)
                              ? 'bg-white shadow-2xs'
                              : 'bg-zinc-100 text-zinc-600'
                          }`}>
                            {String.fromCharCode(65 + optionIndex)}
                          </span>
                          <span className="flex-1 break-words">{opt}</span>
                          {statusIcon}
                        </button>
                      );
                    })
                  ) : (
                    <input
                      type="text"
                      autoFocus
                      placeholder="Shkruaj përgjigjen këtu dhe shtyp Enter..."
                      className="col-span-full h-13 rounded-xl border border-zinc-200 bg-white px-4 text-base text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-[#DD0000] focus:ring-2 focus:ring-red-100"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleAnswer(event.target.value, current.correct_answer);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Progress bar at card bottom */}
              <div className="h-1.5 w-full bg-zinc-100">
                <div
                  className="h-full bg-zinc-950 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </section>

            {/* Right Sidebar: HUD & Live Stats */}
            <aside className="lg:col-span-4 space-y-4">
              {/* Timer HUD Card */}
              <div className={`overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition ${
                urgentTime ? 'border-red-300 ring-2 ring-red-100' : 'border-zinc-200/80'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">Koha e Mbetur</span>
                  <Clock3 size={18} className={urgentTime ? 'text-red-600 animate-pulse' : 'text-zinc-500'} />
                </div>
                <div className={`mt-3 text-4xl font-black tabular-nums tracking-tight ${
                  urgentTime ? 'text-red-600 animate-pulse' : 'text-zinc-900'
                }`}>
                  00:{String(timeLeft).padStart(2, '0')}
                </div>

                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${
                      urgentTime ? 'bg-[#DD0000]' : 'bg-[#FFCE00]'
                    }`}
                    style={{ width: `${(timeLeft / DURATION) * 100}%` }}
                  />
                </div>
              </div>

              {/* Current Score Card */}
              <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">Përgjigje të Sakta</span>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-xs font-bold text-emerald-600">
                    <Check size={14} />
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-zinc-900">{liveCorrectCount}</span>
                  <span className="text-xs text-zinc-400">nga {idx} të përfunduara</span>
                </div>

                <div className="flex items-center justify-between rounded-xl bg-amber-50/70 p-3 text-xs font-bold text-amber-800">
                  <span className="flex items-center gap-1.5">
                    <Sparkles size={14} className="text-amber-600" /> XP i mbledhur:
                  </span>
                  <span className="font-extrabold text-amber-900">+{liveCorrectCount * 5} XP</span>
                </div>

                <div className="flex items-center justify-between text-xs text-zinc-500 pt-1">
                  <span>Synimi:</span>
                  <span className="font-bold text-zinc-800">Mbi {bestScore} pyetje</span>
                </div>
              </div>
            </aside>
          </div>
        )}

        {/* =========================================================
            PHASE 4: DONE - Celebratory Recap & Results Breakdown
           ========================================================= */}
        {phase === 'done' && result && (
          <section className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
            <div aria-hidden="true" className="flex h-1 shrink-0">
              <span className="flex-1 bg-zinc-950" />
              <span className="flex-1 bg-[#DD0000]" />
              <span className="flex-1 bg-[#FFCE00]" />
            </div>

            <div className="p-7 sm:p-10">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 shadow-2xs">
                <Trophy size={32} />
              </div>

              <div className="mt-5 text-center">
                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#DD0000]">Sfida Përfundoi</p>
                <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-zinc-900 sm:text-3xl">
                  {result.score >= 80 ? 'Punë e jashtëzakonshme!' : 'Punë e mirë, vazhdo kështu!'}
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  Ja si performove gjatë kësaj minute në gjermanisht.
                </p>
              </div>

              {/* Stats Grid */}
              <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-zinc-200/70 bg-[#fafafa] p-4 text-center">
                  <span className="block text-3xl font-black text-zinc-900">
                    {result.correct} <span className="text-base font-bold text-zinc-400">/ {result.total}</span>
                  </span>
                  <span className="mt-1 block text-xs font-bold text-zinc-500">Të sakta</span>
                </div>

                <div className="rounded-xl border border-zinc-200/70 bg-[#fafafa] p-4 text-center">
                  <span className="block text-3xl font-black text-zinc-900">{result.score}%</span>
                  <span className="mt-1 block text-xs font-bold text-zinc-500">Saktësia</span>
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
                  <span className="block text-3xl font-black text-amber-800">+{result.xp_awarded || 0}</span>
                  <span className="mt-1 block text-xs font-bold text-amber-700">Pikë XP të fituara</span>
                </div>
              </div>

              {result.correct > bestScore && (
                <div className="mx-auto mt-5 flex max-w-sm items-center justify-center gap-2 rounded-full bg-amber-100/70 px-4 py-2 text-xs font-extrabold text-amber-900 shadow-2xs">
                  <Sparkles size={15} className="text-amber-700" />
                  Rekord i ri personal! E tejkalove rezultatin e vjetër ({bestScore}).
                </div>
              )}

              {/* Answers Review List */}
              {answers.length > 0 && (
                <div className="mx-auto mt-8 max-w-3xl space-y-3 text-left">
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">
                    Rishikimi i Pyetjeve ({answers.length})
                  </h3>
                  <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                    {answers.map((ans, aIdx) => (
                      <div
                        key={aIdx}
                        className={`flex items-start justify-between gap-3 rounded-xl border p-3 text-xs ${
                          ans.is_correct
                            ? 'border-emerald-200 bg-emerald-50/40 text-emerald-950'
                            : 'border-red-200 bg-red-50/40 text-red-950'
                        }`}
                      >
                        <div className="space-y-1">
                          <span className="font-bold text-zinc-800">{aIdx + 1}. {ans.question_text}</span>
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <span>Përgjigjja jote: <strong className={ans.is_correct ? 'text-emerald-700' : 'text-red-700'}>{ans.user_answer}</strong></span>
                            {!ans.is_correct && (
                              <span>• Saktë: <strong className="text-emerald-700">{ans.correct_answer}</strong></span>
                            )}
                          </div>
                        </div>
                        <span className="shrink-0 pt-0.5">
                          {ans.is_correct ? (
                            <CheckCircle2 size={16} className="text-emerald-600" />
                          ) : (
                            <XCircle size={16} className="text-red-600" />
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={startGame}
                  className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-[#DD0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0000] focus-visible:ring-offset-2"
                >
                  <RotateCcw size={16} /> Provo Përsëri
                </button>
                <button
                  type="button"
                  onClick={() => setPhase('idle')}
                  className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-5 py-3 text-sm font-bold text-zinc-700 transition hover:bg-zinc-50"
                >
                  Kthehu te Fillimi
                </button>
              </div>
            </div>
          </section>
        )}

        {xpToast && (
          <XPToast amount={xpToast.amount} reason={xpToast.reason} onDone={() => setXpToast(null)} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default TimedChallengePage;
